import { NgModule } from '@angular/core';
import { SharedModule } from '../shared/shared.module';
import { OrderRoutingModule, routingComponents } from './order-routing.module';
import { ProductDescriptionComponent } from './steps/product-description/product-description.component';
import { ProductDescriptionFallbackComponent } from './components/product-description-fallback/product-description-fallback.component';
import { SingleInsurantComponent } from './steps/single-insurant/single-insurant.component';
import { OrderLoginComponent } from './components/order-login/order-login.component';
import { BillingAddressComponent } from './steps/billing-address/billing-address.component';
import { BillingAddressFormComponent } from './components/billing-address-form/billing-address-form.component';
import { BillingAddressDisplayComponent } from './components/billing-address-display/billing-address-display.component';
import { PaymentDataComponent } from './steps/payment-data/payment-data.component';
import { PaymentDataDisplayComponent } from './components/payment-data-display/payment-data-display.component';
import { SubmitComponent } from './steps/submit/submit.component';
import { InsuredObjectDynamicFormComponent } from './components/insured-object-dynamic-form/insured-object-dynamic-form.component';
import { InsuredObjectsComponent } from './steps/insured-objects/insured-objects.component';
import { PriceDisplayCardComponent } from './components/price-display-card/price-display-card.component';
import { InlinePriceDisplayComponent } from './components/inline-price-display/inline-price-display.component';
import { ContractStartPickerComponent } from './components/contract-start-picker/contract-start-picker.component';
import { ContractStartDateComponent } from './steps/contract-start-date/contract-start-date.component';
import { InsurantDynamicFormComponent } from './components/insurant-dynamic-form/insurant-dynamic-form.component';
import { InsurantDynamicFormContainerComponent } from './components/insurant-dynamic-form-container/insurant-dynamic-form-container.component';
import { OrderSummaryComponent } from './components/order-summary/order-summary.component';
import { SummaryInsuredObjectsComponent } from './components/summary-insured-objects/summary-insured-objects.component';
import { InfoSidebarComponent } from './components/info-sidebar/info-sidebar.component';
import { CustomerTransferResolver } from './resolvers/customer-transfer.resolver';
import { SummaryInsurantsComponent } from './components/summary-insurants/summary-insurants.component';
import { ContractPeriodComponent } from './steps/contract-period/contract-period.component';
import { StepToggleComponent } from './components/step-toggle/step-toggle.component';
import { NavigationButtons } from './components/navigation-buttons/navigation-buttons.component';
import { ProductDataResolver } from './resolvers/product-data.resolver';
import { FormToggleComponent } from './components/form-toggle/form-toggle.component';
import { LoggedInUserDetailComponent } from './components/logged-in-user-detail/logged-in-user-detail.component';
import { ContractStartWithPriceComponent } from './steps/contract-start-with-price/contract-start-with-price.component';
import { LandingPageContainerComponent } from './components/landing-page-container/landing-page-container.component';
import { TitlesResolver } from './resolvers/titles.resolver';
import { SingleInsurantFormComponent } from './components/single-insurant-form/single-insurant-form.component';
import { InfoBoxComponent } from '../shared/components/info-box/info-box.component';
import { RequiredInfoComponent } from '../shared/components/required-info/required-info.component';
import { InfoTooltipComponent } from '../shared/components/info-tooltip/info-tooltip.component';
import { HeroComponent } from '../shared/components/hero/hero.component';
import { JsonLdComponent } from '../shared/json-ld/json-ld.component';
import { FormLabelPipe } from '../shared/pipes/form-label.pipe';
import { ManglePipe } from '../shared/pipes/mangle.pipe';
import { CommaToDotPipe } from '../shared/pipes/comma-to-dot.pipe';
import { TitleSelectorComponent } from '../shared/components/title-selector/title-selector.component';
import { TogglePasswordComponent } from '../shared/components/toggle-password/toggle-password.component';
import { StepHostDirective } from '../shared/directives/step-host.directive';
import { CtrlDateInputDirective, CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';
import { ErrorMessagesComponent } from '../shared/components/error-messages/error-messages.component';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { SettlementPeriodSwitchComponent } from './components/settlement-period-switch/settlement-period-switch.component';
import { CtrlTooltipsModule } from '@co-3/ctrl-ui/tooltips';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { DisplayPriceComponent } from 'app/shared/components/display-price/display-price.component';
import { CollapsibleCheckboxComponent } from 'app/shared/components/collapsible-checkbox/collapsible-checkbox.component';
import { PaymentDataFormComponent } from './components/payment-data-form/payment-data-form.component';
import { PriceInputDirective } from 'app/shared/directives/price-input.directive';

const steps = [
    ContractStartWithPriceComponent,
    ContractPeriodComponent,
    SingleInsurantComponent,
    BillingAddressComponent,
    PaymentDataComponent,
    SubmitComponent,
    InsuredObjectsComponent,
    ContractStartDateComponent,
];

@NgModule({
    declarations: [
        // order steps
        steps,
        // routable components(views)
        routingComponents,
        // other order specific components
        ProductDescriptionComponent,
        ProductDescriptionFallbackComponent,
        OrderLoginComponent,
        BillingAddressFormComponent,
        BillingAddressDisplayComponent,
        PaymentDataDisplayComponent,
        InsuredObjectDynamicFormComponent,

        InsurantDynamicFormComponent,
        InsurantDynamicFormContainerComponent,
        LoggedInUserDetailComponent,
        OrderSummaryComponent,
        SummaryInsuredObjectsComponent,
        StepToggleComponent,
        SummaryInsurantsComponent,
        FormToggleComponent,
        LandingPageContainerComponent,
    ],
    imports: [
        SharedModule,
        OrderRoutingModule,
        InfoBoxComponent,
        InfoTooltipComponent,
        RequiredInfoComponent,
        HeroComponent,
        JsonLdComponent,
        FormLabelPipe,
        ManglePipe,
        CommaToDotPipe,
        TitleSelectorComponent,
        TogglePasswordComponent,
        StepHostDirective,
        CtrlDateInputDirective,
        ErrorMessagesComponent,
        InfoSidebarComponent,
        ReactiveLabelDirective,
        SingleInsurantFormComponent,
        NavigationButtons,
        SettlementPeriodSwitchComponent,
        CtrlErrorMessageDirective,
        CtrlTooltipsModule,
        CtrlIconComponent,
        DisplayPriceComponent,
        CollapsibleCheckboxComponent,
        ContractStartPickerComponent,
        PaymentDataFormComponent,
        InlinePriceDisplayComponent,
        PriceDisplayCardComponent,
        PriceInputDirective,
    ],
    providers: [CustomerTransferResolver, ProductDataResolver, TitlesResolver],
    exports: [
        ContractStartPickerComponent,
        InsurantDynamicFormContainerComponent,
        NavigationButtons,
        InlinePriceDisplayComponent,
        PriceDisplayCardComponent,
        SingleInsurantFormComponent,
    ],
})
export class OrderModule {}
